import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Layers, CropPortrait, CropLandscape, CropSquare, Check } from '@mui/icons-material';
import { MiniPanelProps, ExpandedPanelProps } from '../../../../types/steps';
import MiniPanel from './MiniPanel';
import ExpandedPanel from './ExpandedPanel';
import { Layout, BorderStyle } from '../../types';
import { LayoutInfo } from '../../constants';
import { setLayout, setBorder, useLayout } from '../../../../state/map';
import { useAppDispatch } from '../../../../state/types';

const layoutIcons = {
  [Layout.PORTRAIT]: CropPortrait,
  [Layout.LANDSCAPE]: CropLandscape,
  [Layout.SQUARE]: CropSquare,
};

export const LayersMiniPanel: React.FC<MiniPanelProps> = ({ isActive, onExpand }) => {
  const currentLayout = useLayout();
  const dispatch = useAppDispatch();

  const handleLayoutClick = (layout: Layout, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the panel
    dispatch(setLayout(layout));
    dispatch(setBorder(BorderStyle.NONE));
  };

  return (
    <MiniPanel isActive={isActive} onExpand={onExpand}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Layers sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {LayoutInfo[currentLayout].name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {Object.values(Layout).map((layout) => {
            const Icon = layoutIcons[layout];
            return (
              <Chip
                key={layout}
                icon={<Icon />}
                label=""
                size="small"
                onClick={(e) => handleLayoutClick(layout, e)}
                variant={currentLayout === layout ? 'filled' : 'outlined'}
                color={currentLayout === layout ? 'primary' : 'default'}
                sx={{ 
                  minWidth: '32px',
                  height: '28px',
                  '& .MuiChip-icon': { margin: 0 }
                }}
              />
            );
          })}
        </Box>
      </Box>
    </MiniPanel>
  );
};

export const LayersExpandedPanel: React.FC<ExpandedPanelProps> = ({ 
  isActive, 
  onMinimize, 
  onClose 
}) => {
  const currentLayout = useLayout();
  const dispatch = useAppDispatch();

  const handleLayoutClick = (layout: Layout) => {
    dispatch(setLayout(layout));
    dispatch(setBorder(BorderStyle.NONE));
  };

  const layoutOptions = Object.values(Layout).map((layout) => ({
    value: layout,
    label: LayoutInfo[layout].name,
    Icon: layoutIcons[layout],
  }));

  return (
    <ExpandedPanel 
      isActive={isActive} 
      onMinimize={onMinimize} 
      onClose={onClose}
      title="Layout Options"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {layoutOptions.map(({ value, label, Icon }) => (
            <Box
              key={value}
              onClick={() => handleLayoutClick(value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                bgcolor: currentLayout === value ? 'action.selected' : 'background.default',
                borderRadius: '12px',
                border: '2px solid',
                borderColor: currentLayout === value ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateX(4px)',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Icon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {label}
                </Typography>
              </Box>
              {currentLayout === value && (
                <Check sx={{ color: 'primary.main' }} />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ 
          p: 2, 
          bgcolor: 'info.light',
          borderRadius: '12px'
        }}>
          <Typography variant="body2">
            💡 Changing the layout will reset any border customizations
          </Typography>
        </Box>
      </Box>
    </ExpandedPanel>
  );
};
